import { useCallback, useEffect, useMemo, useState } from 'react';
import { PvxPartnerDetailsFormData } from '../types/partnerDetails';
import { PartnerEntityType } from '../constants';

export type FormErrors = Partial<Record<keyof PvxPartnerDetailsFormData, string>>;
export type TouchedFields = Partial<Record<keyof PvxPartnerDetailsFormData, boolean>>;

const INITIAL_PARTNER_DETAILS: PvxPartnerDetailsFormData = {
  entityType: PartnerEntityType.COMPANY,
  name: '',
  address: '',
  companyHQ: '',
  email: '',
  username: '',
  partnerType: '',
  pvxPocEmail: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Validator = (value: string, formData: PvxPartnerDetailsFormData) => string;

const CREATE_FIELD_VALIDATORS: Partial<Record<keyof PvxPartnerDetailsFormData, Validator>> = {
  entityType: (value) => (value ? '' : 'Type is required'),
  name: (value) => (value.trim() ? '' : 'Name is required'),
  email: (value) => {
    if (!value.trim()) return 'Email is required';
    if (!EMAIL_REGEX.test(value)) return 'Please enter a valid email address';
    return '';
  },
  username: (value) => (value.trim() ? '' : 'Username is required'),
  partnerType: (value) => (value ? '' : 'Partner Type is required'),
};

/** Edit mode skips email/username — those are immutable after creation. */
const EDIT_FIELD_VALIDATORS: Partial<Record<keyof PvxPartnerDetailsFormData, Validator>> = {
  name: CREATE_FIELD_VALIDATORS.name,
  entityType: CREATE_FIELD_VALIDATORS.entityType,
};

const validateField = (
  field: keyof PvxPartnerDetailsFormData,
  value: string,
  formData: PvxPartnerDetailsFormData,
  validators: Partial<Record<keyof PvxPartnerDetailsFormData, Validator>>,
): string => validators[field]?.(value, formData) ?? '';

type UsePvxPartnerDetailsFormParams = {
  initialValues?: PvxPartnerDetailsFormData;
  isEditMode?: boolean;
};

export const usePvxPartnerDetailsForm = ({
  initialValues,
  isEditMode = false,
}: UsePvxPartnerDetailsFormParams = {}) => {
  const [partnerDetails, setPartnerDetails] =
    useState<PvxPartnerDetailsFormData>(INITIAL_PARTNER_DETAILS);
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({});
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const validators = isEditMode ? EDIT_FIELD_VALIDATORS : CREATE_FIELD_VALIDATORS;
  const validatedFields = Object.keys(validators) as Array<keyof PvxPartnerDetailsFormData>;

  useEffect(() => {
    if (initialValues) {
      setPartnerDetails(initialValues);
    }
  }, [initialValues]);

  const handleFieldChange = useCallback(
    (field: keyof PvxPartnerDetailsFormData, value: string) => {
      const updatedFormData = { ...partnerDetails, [field]: value };
      setPartnerDetails(updatedFormData);

      if (touchedFields[field]) {
        setFormErrors((prev) => ({
          ...prev,
          [field]: validateField(field, value, updatedFormData, validators),
        }));
      }
    },
    [partnerDetails, touchedFields, validators],
  );

  const handleFieldBlur = useCallback(
    (field: keyof PvxPartnerDetailsFormData) => {
      setTouchedFields((prev) => ({ ...prev, [field]: true }));
      setFormErrors((prev) => ({
        ...prev,
        [field]: validateField(field, partnerDetails[field] ?? '', partnerDetails, validators),
      }));
    },
    [partnerDetails, validators],
  );

  const validateAll = useCallback((): boolean => {
    const allTouched = validatedFields.reduce<TouchedFields>(
      (acc, field) => ({ ...acc, [field]: true }),
      {},
    );
    setTouchedFields(allTouched);

    const errors = validatedFields.reduce<FormErrors>(
      (acc, field) => ({
        ...acc,
        [field]: validateField(field, partnerDetails[field] ?? '', partnerDetails, validators),
      }),
      {},
    );
    setFormErrors(errors);

    return Object.values(errors).every((error) => !error);
  }, [partnerDetails, validatedFields, validators]);

  const isFormValid = useMemo(
    () =>
      validatedFields.every(
        (field) => !validateField(field, partnerDetails[field] ?? '', partnerDetails, validators),
      ),
    [partnerDetails, validatedFields, validators],
  );

  return {
    partnerDetails,
    touchedFields,
    formErrors,
    isFormValid,
    handleFieldChange,
    handleFieldBlur,
    validateAll,
  };
};
